import React, { useContext, useEffect, useState } from "react";
import { StyleSheet, TouchableOpacity, TextInput } from "react-native";
import { Text, View } from "../../shared/Themed";
import FeatherIcon from "react-native-vector-icons/Feather";
import { ReadOnlyProps } from "../../../utils/templates";
import DeviceOrientationContext from "../../context/DeviceOrientationContext";
import { exportDataToExcel } from "../../../utils/exportDataToExcel";
import { useAppTheme } from "../../../hooks/useAppTheme";
import type { ThemeColors } from "../../../utils/constants/colors";

interface Props {
  childern?: React.ElementType;
  titleName: string;
  isSmallDevice?: boolean;
  isMobileDevice?: boolean;
  isEditable?: boolean;
  ref?: any;
  isResetFields?: boolean;
  handleSearch: (searchText: string) => void;
  setRefresh: (event: any) => void;
  setEditable?: (event: any) => void;

  //for excel download
  dataToExport?: Array<unknown>;
  fileName?: string;
  sheetName?: string;

  // J
  padding?: boolean;
  disableTitle?: boolean;
  noBorder?: boolean;
  _searchText: string;
  onSearchTextChange?: (text: string) => void;
}

export const DynamicLabelBannerWithSearchBar = (
  props: ReadOnlyProps<Props>,
) => {
  const { colors } = useAppTheme();
  const styles = createStyles(colors);
  const Children = (props.childern || {}) as React.ElementType;
  const { isSmallDevice, isMobileDevice } = useContext(
    DeviceOrientationContext,
  );

  useEffect(() => {
    if (props.isResetFields) {
      props.handleSearch("");
    }
  }, [props.isResetFields]);

  const submitSearch = () => {
    props.handleSearch(props._searchText.trim());
  };

  const isExportAllowed = () => {
    return (
      !isSmallDevice &&
      !isMobileDevice &&
      props.dataToExport &&
      props.dataToExport.length > 0
    );
  };

  const showInlineTitle = !isSmallDevice && !isMobileDevice && props.disableTitle == undefined;

  return (
    <View>
      <View style={styles.container}>
        {/* Search row */}
        <View
          style={[
            styles.searchRow,
            showInlineTitle && { justifyContent: "space-between" },
            props.isEditable && { backgroundColor: "#FEF3C7" },
          ]}
        >
          {/* Inline title for desktop */}
          {showInlineTitle && (
            <Text style={styles.inlineTitle}>{props.titleName}</Text>
          )}

          {/* Search controls - grouped on right for desktop */}
          <View style={[
            styles.searchControls,
            !showInlineTitle && { width: "100%" },
          ]}>
            {props.isEditable && (
              <TouchableOpacity
                style={styles.cancelButton}
                onPress={() => props.setEditable?.(!props.isEditable)}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity
              style={styles.refreshButton}
              onPress={() => {
                props.setRefresh(true);
              }}
            >
              <FeatherIcon name="refresh-cw" size={15} color="#6B7280" />
            </TouchableOpacity>

            <View style={[
              styles.searchContainer,
              showInlineTitle && styles.searchContainerCompact,
            ]}>
              <FeatherIcon
                name="search"
                size={16}
                color="#9CA3AF"
                style={{ marginRight: 8 }}
              />
              <TextInput
                ref={props.ref}
                style={styles.searchInput}
                value={props._searchText || ""}
                placeholder="Search..."
                placeholderTextColor={colors.textMuted}
                onChangeText={(text) => props.onSearchTextChange?.(text)}
                onSubmitEditing={() =>
                  props._searchText.trim().length < 3 ? {} : submitSearch()
                }
              />
            </View>

            {isExportAllowed() && (
              <TouchableOpacity
                style={styles.exportButton}
                onPress={() =>
                  exportDataToExcel(
                    props.dataToExport || [],
                    props.fileName,
                    props.sheetName,
                  )
                }
              >
                <FeatherIcon name="download" size={15} color="#6B7280" />
              </TouchableOpacity>
            )}
          </View>
        </View>

        {props.childern && <Children {...props} />}
      </View>
    </View>
  );
};

function createStyles(colors: ThemeColors) {
  return StyleSheet.create({
    container: {
      width: "100%",
    },
    titleRow: {
      paddingHorizontal: 4,
      paddingBottom: 8,
    },
    title: {
      fontSize: 14,
      fontWeight: "600",
      color: "#374151",
    },
    inlineTitle: {
      fontSize: 14,
      fontWeight: "700",
      color: colors.text,
      flexShrink: 0,
    },
    searchRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      width: "100%",
      paddingTop: 10,
      paddingHorizontal: 12,
    },
    searchControls: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
    },
    searchContainerCompact: {
      width: 250,
      flex: undefined,
    },
    searchContainer: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 10,
      paddingHorizontal: 12,
      height: 40,
    },
    searchInput: {
      flex: 1,
      fontSize: 14,
      color: colors.text,
      height: 40,
      outlineStyle: "none",
    } as any,
    refreshButton: {
      width: 40,
      height: 40,
      borderRadius: 10,
      backgroundColor: colors.surfaceSubtle,
      justifyContent: "center",
      alignItems: "center",
      borderWidth: 1,
      borderColor: colors.border,
    },
    exportButton: {
      width: 40,
      height: 40,
      borderRadius: 10,
      backgroundColor: colors.surfaceSubtle,
      justifyContent: "center",
      alignItems: "center",
      borderWidth: 1,
      borderColor: colors.border,
    },
    cancelButton: {
      paddingVertical: 8,
      paddingHorizontal: 14,
      borderRadius: 8,
      backgroundColor: colors.textSecondary,
    },
    cancelButtonText: {
      color: "#fff",
      fontSize: 13,
      fontWeight: "600",
    },
  });
}

